/**
 * Step 6 · Processing (ux-flows §2 step 6, design-spec/07 plate 05).
 *
 * "Never a dead spinner." Thirty to sixty seconds of real AI work (transcribe → quality gate →
 * extract → moderate → draft), made human by a three-beat sequence:
 *
 *   "Listening to what you said…" → "Pulling out the key points…" (bullets appear one by one)
 *   → "Writing the letter…"
 *
 * **The reveal is EARNED, not timed.** The poll returns the transcript as soon as transcription
 * finishes and the key points as soon as extraction does, so each beat is derived from data that
 * actually arrived (see `beatFor`). A timer pretending to be progress would be the same lie as a
 * spinner, just a prettier one — and it would desynchronise the moment the pipeline ran long.
 *
 * Where it can send the referee (all four poll states are handled, none is a dead end):
 *  - `drafted`            → Review.
 *  - `needs_review`       → the calm under-review page. NOT an error: a human is checking, and
 *                           we email them. The divert REASON is never surfaced (see below).
 *  - `rerecord_requested` → back to Record; the quality gate could not use the audio. The state
 *                           machine allows `rerecord_requested → recorded`, so this is a real
 *                           second take, not a restart of the whole favour.
 *  - `processing`         → keep polling (this also covers `recorded` and `failed`, which the
 *                           backend deliberately folds into `processing`: `failed` is an
 *                           operational holding state the sweeps re-drive, so "still working on
 *                           it" is the honest answer, not a lie).
 *
 * The `needs_review` divert reason (moderation categories, `no_key_points`, …) is deliberately
 * NOT on the wire and must not be inferred into the copy: telling a referee doing a favour that
 * they tripped an abuse classifier would be hostile, and it would leak the classifier.
 *
 * A poll error of kind `unauthorized` also leaves — it is the one failure "still trying" must not
 * answer. The transport has already run the designed recovery (re-post `/accept`, retry), so one
 * surfacing here means the request left the resumable states while the referee watched (cancelled,
 * expired, signed in another tab) and nothing is coming. The screen stops polling — every further
 * tick would re-post `/accept` against a dead request and drain the selector's rate-limit budget —
 * and re-reads the context so the step guard relocates them to the true terminal page. Any OTHER
 * poll error keeps trying calmly: a flaky phone connection is the likely cause, and the recording
 * is safe server-side.
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { RefereeApiError, type DraftState } from '@/lib/api-types';
import { beatFor, DRAFT_POLL_INTERVAL_MS, refereeDraftQueryKey } from '@/lib/draft-query';
import { cn } from '@/lib/utils';
import { refereeContextQueryKey, useSession } from '@/session/useSession';
import type { RefereeStep } from '@/session/steps';

/** Where each non-processing poll state sends the referee. `processing` stays put. */
const NEXT_STEP: Record<DraftState['status'], RefereeStep | undefined> = {
  processing: undefined,
  drafted: 'review',
  needs_review: 'under-review',
  rerecord_requested: 'record',
};

/**
 * A 401 the transport could not heal by re-minting the session (see `RefereeApiError`): it has
 * already re-posted `/accept` and retried before surfacing this, so it is a settled answer about
 * the request's state, not a blip a retry or another tick could ride out.
 */
function isUnhealedSession(error: unknown): boolean {
  return error instanceof RefereeApiError && error.kind === 'unauthorized';
}

export function Processing() {
  const { token } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Navigation is a side effect of a poll result; guard it so a re-render mid-transition (or a
  // poll that resolves while we're already leaving) cannot fire it twice.
  const leaving = useRef(false);

  const draft = useQuery({
    queryKey: refereeDraftQueryKey(token),
    queryFn: () => apiClient.pollDraft(token),
    enabled: token.length > 0,
    // Stop asking once the pipeline has reached a state that moves the referee on — or once the
    // session is known-dead: with the re-mint refused, every further tick is a futile
    // 401 → /accept → 409 round-trip against a request that has already left.
    refetchInterval: (query) => {
      if (query.state.data && query.state.data.status !== 'processing') return false;
      if (isUnhealedSession(query.state.error)) return false;
      return DRAFT_POLL_INTERVAL_MS;
    },
    staleTime: 0,
    // A connection blip is worth two more tries; an unhealed 401 is not — retrying it only delays
    // the leave-for-truth below while re-posting /accept each attempt.
    retry: (failureCount, error) => failureCount < 2 && !isUnhealedSession(error),
  });

  const status = draft.data?.status;

  useEffect(() => {
    if (!status || leaving.current) return;
    const next = NEXT_STEP[status];
    if (!next) return;

    leaving.current = true;
    void (async () => {
      // Refresh the CONTEXT before navigating, not after. The step guard decides from the context
      // query, not from this poll, so navigating first lands on a step the guard still thinks is
      // out of reach (`processing` cannot open `review`) and it redirects straight back here —
      // a visible bounce that only settles once the refetch lands. Awaiting the invalidation
      // first means the guard already agrees by the time we move. NB the flow does still arrive
      // either way (the bounce self-heals), so this ordering buys the absence of a flicker, not
      // correctness — don't reorder it expecting a test to stop you.
      await queryClient.invalidateQueries({ queryKey: refereeContextQueryKey(token) });
      navigate(`/r/${token}/${next}`, { replace: true });
    })();
  }, [status, token, navigate, queryClient]);

  // Leave for truth on an unhealed session (mirrors Review's draft-poll handling): re-read the
  // context and let the step guard — the only authority on where the request actually went —
  // relocate them to the true terminal page. The poll above has already stopped ticking.
  const draftError = draft.error;
  useEffect(() => {
    if (!isUnhealedSession(draftError)) return;
    void queryClient.invalidateQueries({ queryKey: refereeContextQueryKey(token) });
  }, [draftError, queryClient, token]);

  const beat = beatFor(draft.data);
  const keyPoints = draft.data?.keyPoints ?? [];

  return (
    <div className="flex flex-1 flex-col">
      <h1 className="font-display text-[22px] font-semibold leading-[1.2] tracking-[-0.02em] text-ink text-balance">
        Turning your words into a letter — we invent nothing.
      </h1>
      <p className="mt-2 font-body text-[14px] leading-relaxed text-ink-70 text-pretty">
        This usually takes under a minute. You can keep this page open.
      </p>

      {/* The whole sequence is one polite live region: a screen reader hears each beat as it is
          reached, and the bullets as they appear, without the beats stealing focus. */}
      <div className="mt-6 flex flex-col gap-3" aria-live="polite" aria-busy={status === 'processing'}>
        <Beat state={beat > 0 ? 'done' : 'live'}>Listening to what you said…</Beat>

        <Beat state={beat > 1 ? 'done' : beat === 1 ? 'live' : 'wait'}>Pulling out the key points…</Beat>

        {(keyPoints.length > 0 || beat === 1) && (
          <ul className="ml-[26px] flex flex-col gap-[7px]">
            {keyPoints.map((point) => (
              <li key={point} className="flex items-start gap-[7px] font-body text-[12.5px] leading-[1.4] text-ink-70">
                <span className="flex-none pt-[2px] text-sage">
                  <CheckIcon className="h-[13px] w-[13px]" />
                </span>
                {point}
              </li>
            ))}
            {/* One shimmering placeholder for the point still being pulled out — the mock's
                skeleton. Hidden from assistive tech: there is no content to announce yet. */}
            {beat < 2 && (
              <li aria-hidden="true" className="skel mt-[2px] h-[11px] w-[72%] rounded-[6px]" />
            )}
          </ul>
        )}

        <Beat state={beat === 2 ? 'live' : 'wait'}>Writing the letter…</Beat>
      </div>

      <div className="mt-auto pt-8">
        {draft.isError && !isUnhealedSession(draft.error) ? (
          // The pipeline is server-side and the poll is idempotent, so a failed read is almost
          // always the phone's connection, not the letter. Say so calmly, and keep the effort.
          // NOT for an unhealed session: "still trying" would be a lie — the poll has stopped and
          // the step guard is relocating, so the calm baseline holds for that beat instead.
          <p role="alert" className="text-center text-[12px] font-medium leading-relaxed text-coral-text">
            We&rsquo;re having trouble reaching your letter — your recording is safe. Still trying…
          </p>
        ) : (
          <div className="flex items-center justify-center gap-2 text-[12px] font-medium text-sage-text">
            <ShieldCheckIcon className="h-4 w-4 flex-none" />
            <span>You approve every word before anything is sent</span>
          </div>
        )}
      </div>
    </div>
  );
}

/** One beat of the sequence: done (check), live (pulsing coral dot), or waiting (grey dot). */
function Beat({ state, children }: { state: 'done' | 'live' | 'wait'; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        'flex items-start gap-2.5 font-display text-[13.5px] font-medium leading-[1.4]',
        state === 'wait' ? 'text-ink-quiet' : 'text-ink',
      )}
    >
      {state === 'done' ? (
        <span className="flex-none pt-[1px] text-sage">
          <CheckIcon className="h-4 w-4" />
        </span>
      ) : (
        <span
          className={cn(
            'mx-[5px] mt-[6px] h-[7px] w-[7px] flex-none rounded-full',
            state === 'live' ? 'beat-live bg-coral ring-[3px] ring-coral-tint' : 'bg-ink-38',
          )}
        />
      )}
      {children}
    </div>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ShieldCheckIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M12 2l7 4v6c0 5-3.5 8-7 10-3.5-2-7-5-7-10V6z" stroke="currentColor" strokeWidth={2} strokeLinejoin="round" />
      <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
